import { Slider, Switch } from '@localina/core';
import {
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IRequestModeSwitchWithSliderProps {
    activationPercentage: number | null;
    setActivationPercentage: (newValue: number | null) => void;
}

const RequestModeSwitchWithSlider = (props: IRequestModeSwitchWithSliderProps) => {
    const { t } = useTranslation();

    const [expanded, setExpanded] = useState(props.activationPercentage !== null);
    // the value under the thumb while dragging, so the info follows it before it's committed on release
    const [draggedPercentage, setDraggedPercentage] = useState<number | null>(null);

    const onSwitchToggle = (value: boolean) => {
        setExpanded(value);
        if (!value) {
            props.setActivationPercentage(null);
        }
    };

    useEffect(() => {
        setExpanded(props.activationPercentage !== null);
        setDraggedPercentage(null);
    }, [props.activationPercentage]);

    useEffect(() => {
        if (expanded && props.activationPercentage == null) {
            props.setActivationPercentage(0);
        }
    }, [expanded]);

    // once switched on, the info spells out what the chosen threshold means (0% and 100% are the edge cases)
    const getDescription = (isOn: boolean, percentage: number) => {
        if (!isOn) {
            return t('availability.shiftView.fields.requestModeInfo');
        }
        if (percentage === 0) {
            return t('availability.shiftView.fields.requestModeActiveInfo.allSlots');
        }
        if (percentage === 100) {
            return t('availability.shiftView.fields.requestModeActiveInfo.never');
        }
        return t('availability.shiftView.fields.requestModeActiveInfo.threshold', { percentage });
    };

    // every variant sits in the same cell, only the current one visible, so the info keeps the height of
    // the longest one and neither the switch nor the slider moves while the text changes
    const description = (
        <span className="request-mode-switch-accordion__info">
            <span>{getDescription(expanded, draggedPercentage ?? props.activationPercentage ?? 0)}</span>
            {[
                getDescription(false, 0),
                getDescription(true, 0),
                getDescription(true, 99),
                getDescription(true, 100),
            ].map((text) => (
                <span key={text} className="request-mode-switch-accordion__info-sizer" aria-hidden="true">
                    {text}
                </span>
            ))}
        </span>
    );

    return (
        <div className="request-mode-switch-accordion">
            <MuiAccordion expanded={expanded}>
                <MuiAccordionSummary>
                    <Switch
                        label={t('availability.shiftView.fields.requestMode')}
                        description={description}
                        value={expanded}
                        onChange={onSwitchToggle}
                    />
                </MuiAccordionSummary>
                <MuiAccordionDetails className="accordion-details-style">
                    <Slider
                        name="requestModeCapacityPercentage"
                        value={props.activationPercentage || 0}
                        onChange={props.setActivationPercentage}
                        onDrag={setDraggedPercentage}
                        step={1}
                        valueLabelDisplay={'on'}
                        triggerChangeEventForLastValueOnly
                        marks={[
                            { value: 0, label: '0%' },
                            { value: 25, label: '25%' },
                            { value: 50, label: '50%' },
                            { value: 75, label: '75%' },
                            { value: 100, label: '100%' },
                        ]}
                    />
                </MuiAccordionDetails>
            </MuiAccordion>
        </div>
    );
};

export default RequestModeSwitchWithSlider;
